import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import type {
  ClaimStatus,
  OwnershipClaim,
  OwnershipClaimsParams,
  OwnershipClaimsResponse,
} from '@/client/types/ownership-claims';

export const CLAIMS_BASE = '/api/admin/claims';

export const claimKeys = {
  all: ['ownership-claims-admin'] as const,
  lists: () => [...claimKeys.all, 'list'] as const,
  list: (params: OwnershipClaimsParams) => [...claimKeys.lists(), params] as const,
  detail: (claimid: number) => [...claimKeys.all, 'detail', claimid] as const,
};

const EMPTY_COUNTS: Record<ClaimStatus, number> = { pending: 0, approved: 0, rejected: 0, removed: 0 };

// Admin routes answer with bare `jsonify`; tolerate a `{status, data}` envelope too.
const unwrap = (raw: unknown): any =>
  raw != null && typeof raw === 'object' && 'data' in (raw as object) && 'status' in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const normalizeList = (raw: unknown): OwnershipClaimsResponse => {
  const payload = unwrap(raw) ?? {};
  const claims: OwnershipClaim[] = Array.isArray(payload.claims) ? payload.claims : [];
  // Paging sits under `pagination`, apart from the middleware's trace_id / exec_time.
  const paging = payload.pagination ?? payload;
  return {
    claims,
    total: Number(paging.total ?? claims.length) || 0,
    page: Number(paging.page ?? 1) || 1,
    page_size: Number(paging.page_size ?? 25) || 25,
    counts: { ...EMPTY_COUNTS, ...(payload.counts ?? {}) },
  };
};

const toQuery = (params: OwnershipClaimsParams): string => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const useOwnershipClaimsQuery = (params: OwnershipClaimsParams) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: claimKeys.list(params),
    queryFn: withErrorHandling(
      async () => normalizeList(await request<unknown>(`${CLAIMS_BASE}${toQuery(params)}`)),
      'Could not load ownership claims',
    ),
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useOwnershipClaimQuery = (claimid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: claimKeys.detail(claimid ?? 0),
    queryFn: withErrorHandling(
      async () => {
        const payload = unwrap(await request<unknown>(`${CLAIMS_BASE}/${claimid}`)) ?? {};
        return (payload.claim ?? payload) as OwnershipClaim;
      },
      'Could not load this claim',
    ),
    enabled: Boolean(claimid),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
